import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import type {
	LivePageContentQuery as LivePageContent,
	LivePageContentQueryVariables,
} from './__types__/LivePageContentQuery.ts';
import LivePageContentQuery from './__generated__/LivePageContentQuery.graphql.ts';

export type TriggerViewedAnalyticsParams = {
	contentId: string;
	editPermission: boolean;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	viewMode?: boolean;
	from?: string;
	commentLink?: string;
};

const NO_DATE_DEFINED = -1;
const dayInMilliseconds = 24 * 60 * 60 * 1000;

const calculateDaysSincePageCreated = (createdDate: string | undefined | null): number => {
	// -1 is used here when the date is null to match the route level analytics event in SendTrackViewPageEvent.js
	if (!createdDate) return NO_DATE_DEFINED;

	const timeDifference = new Date().getTime() - new Date(createdDate).getTime();
	return Math.round(timeDifference / dayInMilliseconds);
};

const fetchLivePageContentInfo = async (
	contentId: string,
): Promise<LivePageContent | undefined> => {
	try {
		const { data } = await queryInterop<LivePageContent, LivePageContentQueryVariables>({
			query: LivePageContentQuery,
			context: { single: true },
			variables: {
				contentId,
			},
		});

		return data;
	} catch {
		// NOTE: Intentionally swallow any possible error here as this query is only used for analytics but we still want to track the event
		return undefined;
	}
};

export const triggerViewedAnalytics = async ({
	contentId,
	editPermission,
	createAnalyticsEvent,
	viewMode,
	from,
	commentLink,
}: TriggerViewedAnalyticsParams): Promise<void> => {
	const data = await fetchLivePageContentInfo(contentId);

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			source: 'viewPageScreen',
			actionSubject: 'page',
			action: 'viewed',
			objectType: 'page',
			containerType: 'space',
			containerId: data?.singleContent?.space?.id,
			objectId: contentId,
			attributes: {
				isLivePage: true,
				hasEditPermission: editPermission,
				daysSinceCreated: calculateDaysSincePageCreated(data?.singleContent?.history?.createdDate),
				viewMode,
				from,
				...(commentLink && { commentLink }),
			},
		},
	}).fire();
};
